import moment from "moment";
import MeteoDto from "api/dtos/MeteoDto";
import axiosInstance from "../utils/axiosInstance";
import { constructURL, URLS } from "../utils/baseUrls";
import { constructSearchParams } from "api/utils/constructSearchParams";
import { camelToSnake } from "utils/camelSnakeTransformation";
import { DATE_FORMAT } from "constants/dateFormat";
import { WeatherCumulativeDto, WeatherDto } from "api/dtos/WeatherDto";

const getOpenMeteoWeatherForecast = async (
  fieldId,
  forecastStart,
  forecastEnd
) => {
  const res = await axiosInstance.get(
    `${URLS.externalRequests}/openmeteo-forecast-weather/?field_id=${fieldId}&start_date=${forecastStart}&end_date=${forecastEnd}`
  );

  const days = MeteoDto.fromApi(res.data);
  return days;
};

const getOpenMeteoWeatherHistory = async (fieldId, startDate, endDate) => {
  const res = await axiosInstance.get(
    `${URLS.externalRequests}/openmeteo-historical-weather/?field_id=${fieldId}&start_date=${startDate}&end_date=${endDate}`
  );

  const days = MeteoDto.fromApi(res.data);
  return days;
};

const getWeatherVariables = async () => {
  const url = constructURL(URLS.weatherHistorical, "variables");
  const res = await axiosInstance.get(url);
  return res.data;
};

const getWeatherPerDay = async (
  fieldId,
  startDate,
  endDate,
  variables = []
) => {
  const url = constructURL(URLS.weather);

  // if there is wind-related data requested, request also the wind direction
  const isWindDataRequested = variables.some((v) => v.includes("wind"));
  if (isWindDataRequested) {
    variables.push("windDirectionDominant");
  }

  const variablesSnakeCase = variables.map(camelToSnake);

  const params = constructSearchParams({
    fieldId,
    startDate: startDate,
    endDate: endDate,
    daily: variablesSnakeCase,
  });
  const res = await axiosInstance.get(url, { params });
  const days = WeatherDto.fromApi(res.data);

  return days;
};

const getWeatherCumulative = async ({
  fieldId,
  cropId,
  startDate,
  endDate,
  variables = [],
}) => {
  const url = constructURL(URLS.weather, "biocycle-analytics");

  const variablesSnakeCase = variables.map(camelToSnake);

  const params = constructSearchParams({
    fieldId,
    cropId,
    startDate: startDate,
    endDate: endDate,
    values: variablesSnakeCase,
  });
  const res = await axiosInstance.get(url, { params });
  const data = WeatherCumulativeDto.fromApi(res.data);
  return data;
};

// DEPRECATED, use getWeatherPerDay
const getWeatherForDays = async (fieldId, startDate, endDate) => {
  let days = {};
  const { historyStart, historyEnd, forecastStart, forecastEnd } =
    getDateLimits(startDate, endDate);

  if (historyStart) {
    const weatherPerDay = await getOpenMeteoWeatherHistory(
      fieldId,
      historyStart,
      historyEnd
    );

    days = weatherPerDay;
  }
  if (forecastStart) {
    const weatherPerDay = await getOpenMeteoWeatherForecast(
      fieldId,
      forecastStart,
      forecastEnd
    );

    days = { ...days, ...weatherPerDay };
  }

  return days;
};

export const weatherApi = {
  forecast: {
    external: getOpenMeteoWeatherForecast,
  },
  history: {
    external: getOpenMeteoWeatherHistory,
  },
  variables: getWeatherVariables,
  fetchForDaysNew: getWeatherPerDay,
  fetchForDays: getWeatherForDays,
  fetchCumulative: getWeatherCumulative,
};

const getDateLimits = (startDate, endDate) => {
  let historyStart, historyEnd, forecastStart, forecastEnd;
  const now = moment();
  const startMoment = moment(startDate);
  const endMoment = moment(endDate);

  if (now.clone().isSameOrBefore(startMoment)) {
    forecastStart = moment(startDate).format(DATE_FORMAT);
    forecastEnd = moment(endDate).format(DATE_FORMAT);
  } else if (now.clone().isSameOrBefore(endMoment)) {
    historyStart = moment(startDate).format(DATE_FORMAT);
    historyEnd = now.clone().startOf("day").format(DATE_FORMAT);
    forecastStart = now.clone().startOf("day").format(DATE_FORMAT);
    forecastEnd = moment(endDate).format(DATE_FORMAT);
  } else {
    historyStart = moment(startDate).format(DATE_FORMAT);
    historyEnd = moment(endDate).format(DATE_FORMAT);
  }

  return { historyStart, historyEnd, forecastStart, forecastEnd };
};
